import React, {useCallback} from 'react';
import {GatewayGetSchema} from '../../../../../api/DBSchema/Gateway';
import {SMCCheckbox} from '../../../../../components/Simple/SMCCheckbox';
import {useTranslation} from 'react-i18next';
import PlanEditorInnerContainer from '../../../../../components/Editor/PlanEditorInnerContainer';
import Separator from '../../../../../components/Simple/Sepperator';
import Indentation from '../../../../../components/Simple/Indentation';

import {SMCRadio} from '../../../../../components/Simple/SMCRadio';
import SMCLabel from '../../../../../components/Simple/SMCLabel';
import {BladesPolicy, TraficType} from '../../../../../api/DBSchema/Enums';
import {SMCSelect, SMCOption} from '../../../../../components/Simple/SMCSelect';
import EmptyRow from '../../../../../components/Simple/EmptyRow';
import SMCNote from '../../../../../components/Simple/SMCNote';
import SMCLink from '../../../../../components/Simple/SMCLink';
import EditorLayout from '../../../../../components/Layouts/EditorLayout';
import RowLayout from '../../../../../components/Layouts/RowLayout';
import {SMCToggleSwitch} from "../../../../../components/Simple/SMCToggleSwitch";

const pageSchema = GatewayGetSchema.pages.firewall;
const pageId = pageSchema.pageId;

function BaseFirewall({
    pageData,
    setObjectData,
    pageDisabled,
    setEditorPageId
}) {
    const { t } = useTranslation();

    const managedInSmp           = pageData[pageSchema.manageInSmp];
    const mode                   = pageData[pageSchema.mode];
    const policy                 = pageData[pageSchema.policy];
    const logBlockedTraffic      = pageData[pageSchema.logBlockedTraffic];
    const logBlockedTrafficValue = pageData[pageSchema.logBlockedTrafficValue];
    const logAllowedTraffic      = pageData[pageSchema.logAllowedTraffic];
    const logAllowedTrafficValue = pageData[pageSchema.logAllowedTrafficValue];


    const setPageValue = useCallback((param, newValue) => {
        setObjectData([pageId, param], newValue);
    }, [setObjectData]);

    return (
        <EditorLayout>
            <SMCToggleSwitch
                disabled={pageDisabled}
                checked={managedInSmp}
                onChange={checked => setPageValue(pageSchema.manageInSmp, checked)}
                label={t('SMP.PLAN.MANAGE_IN_SMP')}
            />
            <Separator isMainSeparator/>
            <SMCNote>{t('SMP.DIVIDING_LOG_TRAFFIC_R77_20')}</SMCNote>
            <PlanEditorInnerContainer  managedInSmp={managedInSmp}>
                <RowLayout>
                    <div>
                        <SMCToggleSwitch
                            disabled={pageDisabled || !managedInSmp}
                            checked={mode}
                            onChange={checked => setPageValue(pageSchema.mode, !checked)}
                            label={t('SMP.GATEWAYS.INNER_MENU.FIREWALL')}
                        />
                    </div>
                </RowLayout>
                <SMCLabel bold>{t('SMP.PLAN.SEC_SOFT_BLADES.POLICY')}:</SMCLabel>
                <Indentation indentation={40}>
                    <SMCRadio
                        disabled={pageDisabled || !managedInSmp}
                        checked={policy === BladesPolicy.STRICT}
                        onChange={() => setPageValue(pageSchema.policy, BladesPolicy.STRICT)}
                        label={t('SMP.PLAN.SEC_SOFT_BLADES.STRICT')}
                        tooltip={t('SMP.PLAN.SEC_SOFT_BLADES.TOOLTIP1') + '\n' + t('SMP.PLAN.SEC_SOFT_BLADES.TOOLTIP2')}
                        height={17}
                    />
                    <SMCRadio
                        disabled={pageDisabled || !managedInSmp}
                        checked={policy === BladesPolicy.STANDARD}
                        onChange={() => setPageValue(pageSchema.policy, BladesPolicy.STANDARD)}
                        label={t('SMP.PLAN.SEC_SOFT_BLADES.STANDARD')}
                        tooltip={t('SMP.PLAN.SEC_SOFT_BLADES.TOOLTIP3') + '\n' + t('SMP.PLAN.SEC_SOFT_BLADES.TOOLTIP4') + '\n' + t('SMP.PLAN.SEC_SOFT_BLADES.TOOLTIP5')}
                        height={17}
                    />
                </Indentation>
                
                <EmptyRow />

                <RowLayout spacing="5px">
                    <SMCCheckbox disabled={pageDisabled || !managedInSmp} checked={logBlockedTraffic} onChange={e => setPageValue(pageSchema.logBlockedTraffic, e.target.checked)} />
                    <SMCLabel>{t('SMP.PLAN.SEC_SOFT_BLADES.LOG')}</SMCLabel>
                    <SMCSelect
                        disabled={pageDisabled || !managedInSmp || !logBlockedTraffic}
                        value={logBlockedTrafficValue}
                        onChange={e => setPageValue(pageSchema.logBlockedTrafficValue, e.target.value)}
                    >
                        <SMCOption value={TraficType.ALL}>{t('SMP.ALL')}</SMCOption>
                        <SMCOption value={TraficType.OUTGOING}>{t('SMP.OUTGOING')}</SMCOption>
                        <SMCOption value={TraficType.INCOMING}>{t('SMP.INCOMING_INTERNAL')}</SMCOption>
                    </SMCSelect>
                    <SMCLabel>{t('SMP.PLAN.SEC_SOFT_BLADES.BLOCKED_TRAFFIC')}</SMCLabel>
                </RowLayout>
                <RowLayout spacing="5px">
                    <SMCCheckbox disabled={pageDisabled || !managedInSmp} checked={logAllowedTraffic} onChange={e => setPageValue(pageSchema.logAllowedTraffic, e.target.checked)} />
                    <SMCLabel>{t('SMP.PLAN.SEC_SOFT_BLADES.LOG')}</SMCLabel>
                    <SMCSelect
                        disabled={pageDisabled || !managedInSmp || !logAllowedTraffic}
                        value={logAllowedTrafficValue}
                        onChange={e => setPageValue(pageSchema.logAllowedTrafficValue, e.target.value)}
                    >
                        <SMCOption value={TraficType.ALL}>{t('SMP.ALL')}</SMCOption>
                        <SMCOption value={TraficType.OUTGOING}>{t('SMP.OUTGOING')}</SMCOption>
                        <SMCOption value={TraficType.INCOMING}>{t('SMP.INCOMING_INTERNAL')}</SMCOption>
                    </SMCSelect>
                    <SMCLabel>{t('SMP.PLAN.SEC_SOFT_BLADES.ALLOWED_TRAFFIC')}</SMCLabel>
                </RowLayout>
                <SMCLabel bold>{t('SMP.PLAN.SEC_SOFT_BLADES.NOTES')}:</SMCLabel>
                <Indentation>
                        <SMCLabel>1. {t('SMP.DIVIDING_LOG_TRAFFIC_R77_20')} (B990172271)</SMCLabel>
                </Indentation>

                <EmptyRow />

                <SMCLink underline onClick={() => setEditorPageId(GatewayGetSchema.pages.accessPolicy.pageId)}>
                    {t('SMP.PLAN.SEC_SOFT_BLADES.FIREWALL_ACCESS_POLICY')}
                </SMCLink>
                
                <EmptyRow />

                <SMCLabel><b>{t('SMP.DESCRIPTION')}: </b>{t('SMP.PLAN.SEC_SOFT_BLADES.LINE2')}</SMCLabel>

            </PlanEditorInnerContainer>
        </EditorLayout>
    );
}

export default BaseFirewall;